import { useCallback, useEffect, useRef, useState } from "react";
import { message } from "antd";
import { t } from "i18next";
import { showFallbackErrorToast } from "@apis/http/httpErrorToast";
import {
  createCharacterAsset,
  getCharacterAssetDetail,
  renameCharacterAsset,
  type CharacterAssetRecord,
} from "@apis/http/api";
import { CharacterAssetCreateFormRequestCategory } from "@apis/generated/openapi";
import type { ClipUploadContext, ClipUploadFile } from "@components/ClipUpload/ClipUpload";
import {
  CHARACTER_NAME_MAX_LENGTH,
  MAX_OTHER_CHARACTER_IMAGES,
  type CharacterFormState,
  type CharacterImageFile,
} from "./types";

let slotIdSeq = 0;
const nextSlotUid = () => `char-slot-${++slotIdSeq}-${Date.now()}`;

const emptyForm = (category: CharacterAssetCreateFormRequestCategory): CharacterFormState => ({
  category,
  name: "",
});

/**
 * 把任意可访问的图片 URL 拉成 File，供后端 multipart 上传使用。
 *
 * 用于 "video link" 与 "library" 两种创建模式：先拿到视频封面 URL，再
 * 转换成主图槽位的本地 File，复用统一的 multipart 创建路径。
 */
export const fetchUrlAsFile = async (url: string, fallbackName = "main.jpg", signal?: AbortSignal): Promise<File> => {
  // 如果是同源或带 CORS 头的 CDN，可以直接 fetch；不行就让上层兜底重试
  const response = await fetch(url, { credentials: "omit", signal });
  if (!response.ok) {
    throw new Error(`Failed to fetch image: ${response.status}`);
  }
  const blob = await response.blob();
  const ext = blob.type?.split("/")[1] || "jpg";
  const name = fallbackName.includes(".") ? fallbackName : `${fallbackName}.${ext}`;
  return new File([blob], name, { type: blob.type || "image/jpeg" });
};

/** ClipUpload 受控 onFilesChange 写入主图（首格）+ 其他视角图（其余 N 格） */
const buildSlotsFromFile = (mainFile: ClipUploadFile): CharacterImageFile[] => [mainFile];

interface UseCharacterFormParams {
  characterId?: number | null;
  enabled: boolean;
  defaultCategory: CharacterAssetCreateFormRequestCategory;
  onSuccess?: () => void;
}

export const useCharacterForm = ({ characterId, enabled, defaultCategory, onSuccess }: UseCharacterFormParams) => {
  const [form, setForm] = useState<CharacterFormState>(() => emptyForm(defaultCategory));
  const [imageFiles, setImageFiles] = useState<CharacterImageFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const formRef = useRef(form);
  formRef.current = form;
  const imageFilesRef = useRef(imageFiles);
  imageFilesRef.current = imageFiles;
  const isEditMode = !!characterId;

  // 跟踪本组件创建的 object URL（blob:），在重置/卸载时统一释放，避免内存泄漏。
  const objectUrlsRef = useRef<Set<string>>(new Set());
  const trackObjectUrl = useCallback((url: string) => {
    objectUrlsRef.current.add(url);
  }, []);
  const revokeAllObjectUrls = useCallback(() => {
    objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    objectUrlsRef.current.clear();
  }, []);

  // 卸载时释放所有遗留的 object URL
  useEffect(() => revokeAllObjectUrls, [revokeAllObjectUrls]);

  // 编辑态：拉详情
  useEffect(() => {
    if (!enabled || !characterId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const detail = (await getCharacterAssetDetail({ assetId: characterId })) as CharacterAssetRecord | undefined;
        if (cancelled || !detail) return;
        const main = detail.mainImage?.imageUrl
          ? [
              {
                uid: nextSlotUid(),
                name: "main",
                url: detail.mainImage.imageUrl,
              } satisfies CharacterImageFile,
            ]
          : [];
        const others = (detail.otherImages ?? []).slice(0, MAX_OTHER_CHARACTER_IMAGES).map((img, idx) => ({
          uid: nextSlotUid(),
          name: `other-${idx}`,
          url: img.imageUrl,
        }));
        setForm({
          category: (detail.category as unknown as CharacterAssetCreateFormRequestCategory) ?? defaultCategory,
          name: detail.name ?? "",
        });
        setImageFiles([...main, ...others]);
      } catch {
        if (!cancelled) message.error(t("Failed to load character details"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [characterId, enabled, defaultCategory]);

  // 关闭/卸载时重置
  useEffect(() => {
    if (!enabled) {
      revokeAllObjectUrls();
      setForm(emptyForm(defaultCategory));
      setImageFiles([]);
    }
  }, [enabled, defaultCategory, revokeAllObjectUrls]);

  const updateForm = useCallback((patch: Partial<CharacterFormState>) => {
    setForm((prev) => ({ ...prev, ...patch }));
  }, []);

  const handleFilesChange = useCallback((files: ClipUploadFile[]) => {
    setImageFiles(files as CharacterImageFile[]);
  }, []);

  /**
   * ClipUpload 的 uploadFile：character 接口要求 multipart 一次提交，
   * 因此这里不立刻调上传接口，仅生成本地预览 URL，等 submit 时再随表单一起 POST。
   */
  const uploadFile = useCallback(
    async (file: File, _ctx: ClipUploadContext): Promise<ClipUploadFile> => {
      const previewUrl = URL.createObjectURL(file);
      trackObjectUrl(previewUrl);
      return {
        uid: nextSlotUid(),
        name: file.name,
        url: previewUrl,
        type: file.type,
        size: file.size,
        // File 实例放在 response 里，submit 时取出来塞进 FormData
        response: { file } as { file: File },
      };
    },
    [trackObjectUrl],
  );

  /** 链接 / 视频库流程拿到的封面 URL，转 File 后写入主图槽 */
  const setMainImageFromUrl = useCallback(
    async (url: string, name?: string, signal?: AbortSignal) => {
      const file = await fetchUrlAsFile(url, name ?? "cover.jpg", signal);
      if (signal?.aborted) return;
      const previewUrl = URL.createObjectURL(file);
      trackObjectUrl(previewUrl);
      setImageFiles(
        buildSlotsFromFile({
          uid: nextSlotUid(),
          name: file.name,
          url: previewUrl,
          type: file.type,
          size: file.size,
          response: { file },
        }),
      );
    },
    [trackObjectUrl],
  );

  const submit = useCallback(async (): Promise<boolean> => {
    const currentForm = formRef.current;
    const currentFiles = imageFilesRef.current;

    if (!currentForm.name.trim()) {
      message.error(t("Please enter character name"));
      return false;
    }

    setSubmitting(true);
    try {
      if (characterId) {
        // 当前后端 PUT 仅支持改名，图片改动需待后端补 PATCH
        await renameCharacterAsset({
          query: { assetId: characterId },
          body: { name: currentForm.name.trim() },
        });
        message.success(t("Character updated"));
        onSuccess?.();
        return true;
      }

      const [mainSlot, ...otherSlots] = currentFiles;
      const mainFile = (mainSlot?.response as { file?: File } | undefined)?.file;
      if (!mainFile) {
        message.error(t("Please upload main character image"));
        return false;
      }
      const otherFiles = otherSlots
        .map((slot) => (slot.response as { file?: File } | undefined)?.file)
        .filter((f): f is File => !!f);

      // 注：openapi 类型推断对可选 binary 数组字段会回退为单值，
      // 这里用 as any 兜底；运行时 toFormData 已正确处理 File[] 数组。
      await createCharacterAsset({
        category: currentForm.category,
        name: currentForm.name.trim(),
        mainImage: mainFile,
        otherImages: otherFiles as unknown as File,
      });
      message.success(t("Character created"));
      onSuccess?.();
      return true;
    } catch (err: any) {
      showFallbackErrorToast(err, t("Failed to save character"));
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [characterId, onSuccess]);

  return {
    form,
    updateForm,
    setForm,
    imageFiles,
    setImageFiles,
    handleFilesChange,
    uploadFile,
    setMainImageFromUrl,
    loading,
    submitting,
    isEditMode,
    nameMaxLength: CHARACTER_NAME_MAX_LENGTH,
    submit,
  };
};
